import React from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormFieldEditorDto, FormSectionEditorDto, FormFieldType, PdfFieldConfig, LabelConfig, SectionPdfConfig } from '../../types/formEditor';

interface FieldPropertiesPanelProps {
  selectedField?: FormFieldEditorDto;
  selectedSection?: FormSectionEditorDto;
  onFieldUpdate: (field: FormFieldEditorDto) => void;
  onSectionUpdate: (sectionId: number, pdfConfig: SectionPdfConfig) => void;
  onFieldDelete: (fieldId: number) => void;
}

const fontFamilies = ['Arial', 'Times New Roman', 'Courier New', 'Helvetica', 'Verdana'];
const alignments = ['Left', 'Center', 'Right'];
const verticalAlignments = ['Top', 'Middle', 'Bottom'];

export const FieldPropertiesPanel: React.FC<FieldPropertiesPanelProps> = ({
  selectedField,
  selectedSection,
  onFieldUpdate,
  onSectionUpdate,
  onFieldDelete
}) => {
  // Section Configuration UI
  if (selectedSection && !selectedField) {
    const sectionPdfConfig = selectedSection.pdfConfig || {
      x: 50,
      y: 100,
      width: 495,
      height: 200,
      borderColor: '#cccccc',
      borderWidth: 1,
      showBorder: true,
      pageNumber: 1
    };

    const handleSectionPdfConfigChange = (updates: Partial<SectionPdfConfig>) => {
      onSectionUpdate(selectedSection.id, { ...sectionPdfConfig, ...updates });
    };

    return (
      <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          Section Properties
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Basic Information
        </Typography>

        <TextField
          fullWidth
          label="Section Name"
          value={selectedSection.description}
          margin="dense"
          size="small"
          disabled
          helperText="Section name cannot be changed here"
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Border Configuration
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={sectionPdfConfig.showBorder}
              onChange={(e) => handleSectionPdfConfigChange({ showBorder: e.target.checked })}
            />
          }
          label="Show Border"
        />

        {sectionPdfConfig.showBorder && (
          <>
            <TextField
              fullWidth
              label="Border Color"
              type="color"
              value={sectionPdfConfig.borderColor}
              onChange={(e) => handleSectionPdfConfigChange({ borderColor: e.target.value })}
              margin="dense"
              size="small"
            />

            <TextField
              fullWidth
              label="Border Width"
              type="number"
              value={sectionPdfConfig.borderWidth}
              onChange={(e) => handleSectionPdfConfigChange({ borderWidth: parseFloat(e.target.value) })}
              margin="dense"
              size="small"
              inputProps={{ min: 0.5, max: 10, step: 0.5 }}
            />
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Position & Size
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <TextField
            label="X Position"
            type="number"
            value={sectionPdfConfig.x}
            onChange={(e) => handleSectionPdfConfigChange({ x: parseFloat(e.target.value) })}
            margin="dense"
            size="small"
            inputProps={{ step: 0.5 }}
          />
          <TextField
            label="Y Position"
            type="number"
            value={sectionPdfConfig.y}
            onChange={(e) => handleSectionPdfConfigChange({ y: parseFloat(e.target.value) })}
            margin="dense"
            size="small"
            inputProps={{ step: 0.5 }}
          />
          <TextField
            label="Width"
            type="number"
            value={sectionPdfConfig.width}
            onChange={(e) => handleSectionPdfConfigChange({ width: parseFloat(e.target.value) })}
            margin="dense"
            size="small"
            inputProps={{ step: 0.5 }}
          />
          <TextField
            label="Height"
            type="number"
            value={sectionPdfConfig.height}
            onChange={(e) => handleSectionPdfConfigChange({ height: parseFloat(e.target.value) })}
            margin="dense"
            size="small"
            inputProps={{ step: 0.5 }}
          />
        </Box>

        <TextField
          fullWidth
          label="Page Number"
          type="number"
          value={sectionPdfConfig.pageNumber || 1}
          onChange={(e) => handleSectionPdfConfigChange({ pageNumber: parseInt(e.target.value) || 1 })}
          margin="dense"
          size="small"
          inputProps={{ min: 1 }}
        />
      </Paper>
    );
  }

  // Field Configuration UI
  if (!selectedField) {
    return (
      <Paper sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Select a field or section to edit properties
        </Typography>
      </Paper>
    );
  }

  const pdfConfig = selectedField.pdfConfig || {
    x: 0,
    y: 0,
    width: 150,
    height: 25,
    fontFamily: 'Arial',
    fontSize: 12,
    fontColor: '#000000',
    tabOrder: 0,
    alignment: 'Left',
    isMultiline: false,
    isBold: false,
    isItalic: false,
    pageWidth: 595,
    pageHeight: 842,
    pageNumber: 1
  };

  const labelConfig = pdfConfig.labelConfig || {
    x: pdfConfig.x,
    y: Math.max(10, pdfConfig.y - 20),
    width: 150,
    height: 18,
    fontFamily: 'Arial',
    fontSize: 10,
    fontColor: '#000000',
    isBold: false,
    isItalic: false,
    alignment: 'Left',
    verticalAlignment: 'Top',
    isVisible: true
  };

  const handlePdfConfigChange = (updates: Partial<PdfFieldConfig>) => {
    onFieldUpdate({
      ...selectedField,
      pdfConfig: { ...pdfConfig, ...updates }
    });
  };

  const handleFieldChange = (updates: Partial<FormFieldEditorDto>) => {
    onFieldUpdate({
      ...selectedField,
      ...updates
    });
  };

  const handleLabelConfigChange = (updates: Partial<LabelConfig>) => {
    onFieldUpdate({
      ...selectedField,
      pdfConfig: {
        ...pdfConfig,
        labelConfig: { ...labelConfig, ...updates }
      }
    });
  };

  return (
    <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Field Properties
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {/* Basic Properties */}
      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        Basic Information
      </Typography>

      <TextField
        fullWidth
        label="Field Code"
        value={selectedField.code}
        onChange={(e) => handleFieldChange({ code: e.target.value })}
        margin="dense"
        size="small"
      />

      <TextField
        fullWidth
        label="Description"
        value={selectedField.description}
        onChange={(e) => handleFieldChange({ description: e.target.value })}
        margin="dense"
        size="small"
        multiline
        rows={3}
      />

      <FormControl fullWidth margin="dense" size="small">
        <InputLabel>Field Type</InputLabel>
        <Select
          value={typeof selectedField.fieldType === 'string'
            ? FormFieldType[selectedField.fieldType as keyof typeof FormFieldType]
            : selectedField.fieldType}
          label="Field Type"
          onChange={(e) => handleFieldChange({ fieldType: e.target.value as FormFieldType })}
        >
          <MenuItem value={FormFieldType.String}>Text</MenuItem>
          <MenuItem value={FormFieldType.Multiline}>Multiline Text</MenuItem>
          <MenuItem value={FormFieldType.Bool}>Checkbox</MenuItem>
          <MenuItem value={FormFieldType.Dropdown}>Dropdown</MenuItem>
          <MenuItem value={FormFieldType.Date}>Date</MenuItem>
          <MenuItem value={FormFieldType.DateTime}>Date & Time</MenuItem>
          <MenuItem value={FormFieldType.CheckboxList}>Checkbox List</MenuItem>
          <MenuItem value={FormFieldType.HorizontalRule}>Static Text</MenuItem>
          <MenuItem value={FormFieldType.TextboxWithCheckbox}>Textbox with Checkbox</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Help Text"
        value={selectedField.info || ''}
        onChange={(e) => handleFieldChange({ info: e.target.value })}
        margin="dense"
        size="small"
        multiline
        rows={2}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <TextField
          label="Prefix"
          value={(() => {
            try {
              return selectedField.configurationJson ? JSON.parse(selectedField.configurationJson).prefix || '' : '';
            } catch (e) {
              return '';
            }
          })()}
          onChange={(e) => {
            let config: any = {};
            try {
              config = selectedField.configurationJson ? JSON.parse(selectedField.configurationJson) : {};
            } catch (e) { }
            config.prefix = e.target.value;
            handleFieldChange({ configurationJson: JSON.stringify(config) });
          }}
          margin="dense"
          size="small"
          placeholder="e.g. $"
        />
        <TextField
          label="Suffix"
          value={(() => {
            try {
              return selectedField.configurationJson ? JSON.parse(selectedField.configurationJson).suffix || '' : '';
            } catch (e) {
              return '';
            }
          })()}
          onChange={(e) => {
            let config: any = {};
            try {
              config = selectedField.configurationJson ? JSON.parse(selectedField.configurationJson) : {};
            } catch (e) { }
            config.suffix = e.target.value;
            handleFieldChange({ configurationJson: JSON.stringify(config) });
          }}
          margin="dense"
          size="small"
          placeholder="e.g. mm"
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={selectedField.isRequired}
            onChange={(e) => handleFieldChange({ isRequired: e.target.checked })}
          />
        }
        label="Required Field"
      />

      <Divider sx={{ my: 2 }} />

      {/* PDF Position */}
      <Typography variant="subtitle2" gutterBottom>
        Position & Size
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <TextField
          label="X Position"
          type="number"
          value={pdfConfig.x}
          onChange={(e) => handlePdfConfigChange({ x: parseFloat(e.target.value) })}
          margin="dense"
          size="small"
          inputProps={{ step: 0.5 }}
        />
        <TextField
          label="Y Position"
          type="number"
          value={pdfConfig.y}
          onChange={(e) => handlePdfConfigChange({ y: parseFloat(e.target.value) })}
          margin="dense"
          size="small"
          inputProps={{ step: 0.5 }}
        />
        <TextField
          label="Width"
          type="number"
          value={pdfConfig.width}
          onChange={(e) => handlePdfConfigChange({ width: parseFloat(e.target.value) })}
          margin="dense"
          size="small"
          inputProps={{ step: 0.5 }}
        />
        <TextField
          label="Height"
          type="number"
          value={pdfConfig.height}
          onChange={(e) => handlePdfConfigChange({ height: parseFloat(e.target.value) })}
          margin="dense"
          size="small"
          inputProps={{ step: 0.5 }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Font Properties */}
      <Typography variant="subtitle2" gutterBottom>
        Font & Style
      </Typography>

      <FormControl fullWidth margin="dense" size="small">
        <InputLabel>Font Family</InputLabel>
        <Select
          value={pdfConfig.fontFamily}
          label="Font Family"
          onChange={(e) => handlePdfConfigChange({ fontFamily: e.target.value })}
        >
          {fontFamilies.map((font) => (
            <MenuItem key={font} value={font}>
              {font}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Font Size"
        type="number"
        value={pdfConfig.fontSize}
        onChange={(e) => handlePdfConfigChange({ fontSize: parseFloat(e.target.value) })}
        margin="dense"
        size="small"
        inputProps={{ min: 6, max: 72, step: 0.5 }}
      />

      <TextField
        fullWidth
        label="Font Color"
        type="color"
        value={pdfConfig.fontColor}
        onChange={(e) => handlePdfConfigChange({ fontColor: e.target.value })}
        margin="dense"
        size="small"
      />

      <FormControl fullWidth margin="dense" size="small">
        <InputLabel>Alignment</InputLabel>
        <Select
          value={pdfConfig.alignment}
          label="Alignment"
          onChange={(e) => handlePdfConfigChange({ alignment: e.target.value })}
        >
          {alignments.map((align) => (
            <MenuItem key={align} value={align}>
              {align}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={pdfConfig.isBold}
              onChange={(e) => handlePdfConfigChange({ isBold: e.target.checked })}
              size="small"
            />
          }
          label="Bold"
        />
        <FormControlLabel
          control={
            <Switch
              checked={pdfConfig.isItalic}
              onChange={(e) => handlePdfConfigChange({ isItalic: e.target.checked })}
              size="small"
            />
          }
          label="Italic"
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Tab Order */}
      <Typography variant="subtitle2" gutterBottom>
        Tab Order
      </Typography>

      <TextField
        fullWidth
        label="Tab Order"
        type="number"
        value={pdfConfig.tabOrder}
        onChange={(e) => handlePdfConfigChange({ tabOrder: parseInt(e.target.value) })}
        margin="dense"
        size="small"
        inputProps={{ min: 0 }}
      />

      <TextField
        fullWidth
        label="Page Number"
        type="number"
        value={pdfConfig.pageNumber || 1}
        onChange={(e) => handlePdfConfigChange({ pageNumber: parseInt(e.target.value) || 1 })}
        margin="dense"
        size="small"
        inputProps={{ min: 1 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* Label Configuration */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Label Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch
                checked={labelConfig.isVisible}
                onChange={(e) => handleLabelConfigChange({ isVisible: e.target.checked })}
              />
            }
            label="Show Label"
          />

          {labelConfig.isVisible && (
            <>
              <Typography variant="caption" display="block" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                Position & Size
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField
                  label="X Position"
                  type="number"
                  value={labelConfig.x}
                  onChange={(e) => handleLabelConfigChange({ x: parseFloat(e.target.value) })}
                  margin="dense"
                  size="small"
                  inputProps={{ step: 0.5 }}
                />
                <TextField
                  label="Y Position"
                  type="number"
                  value={labelConfig.y}
                  onChange={(e) => handleLabelConfigChange({ y: parseFloat(e.target.value) })}
                  margin="dense"
                  size="small"
                  inputProps={{ step: 0.5 }}
                />
                <TextField
                  label="Width"
                  type="number"
                  value={labelConfig.width}
                  onChange={(e) => handleLabelConfigChange({ width: parseFloat(e.target.value) })}
                  margin="dense"
                  size="small"
                  inputProps={{ step: 0.5 }}
                />
                <TextField
                  label="Height"
                  type="number"
                  value={labelConfig.height}
                  onChange={(e) => handleLabelConfigChange({ height: parseFloat(e.target.value) })}
                  margin="dense"
                  size="small"
                  inputProps={{ step: 0.5 }}
                />
              </Box>

              <Typography variant="caption" display="block" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                Font & Style
              </Typography>
              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Font Family</InputLabel>
                <Select
                  value={labelConfig.fontFamily}
                  label="Font Family"
                  onChange={(e) => handleLabelConfigChange({ fontFamily: e.target.value })}
                >
                  {fontFamilies.map((font) => (
                    <MenuItem key={font} value={font}>
                      {font}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Font Size"
                type="number"
                value={labelConfig.fontSize}
                onChange={(e) => handleLabelConfigChange({ fontSize: parseFloat(e.target.value) })}
                margin="dense"
                size="small"
                inputProps={{ min: 6, max: 72, step: 0.5 }}
              />

              <TextField
                fullWidth
                label="Font Color"
                type="color"
                value={labelConfig.fontColor}
                onChange={(e) => handleLabelConfigChange({ fontColor: e.target.value })}
                margin="dense"
                size="small"
              />

              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Alignment</InputLabel>
                <Select
                  value={labelConfig.alignment}
                  label="Alignment"
                  onChange={(e) => handleLabelConfigChange({ alignment: e.target.value })}
                >
                  {alignments.map((align) => (
                    <MenuItem key={align} value={align}>
                      {align}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Vertical Alignment</InputLabel>
                <Select
                  value={labelConfig.verticalAlignment || 'Top'}
                  label="Vertical Alignment"
                  onChange={(e) => handleLabelConfigChange({ verticalAlignment: e.target.value })}
                >
                  {verticalAlignments.map((align) => (
                    <MenuItem key={align} value={align}>
                      {align}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={labelConfig.isBold}
                      onChange={(e) => handleLabelConfigChange({ isBold: e.target.checked })}
                      size="small"
                    />
                  }
                  label="Bold"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={labelConfig.isItalic}
                      onChange={(e) => handleLabelConfigChange({ isItalic: e.target.checked })}
                      size="small"
                    />
                  }
                  label="Italic"
                />
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 2 }} />

      {/* Delete Button */}
      <Button
        fullWidth
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() => onFieldDelete(selectedField.id)}
        sx={{ mt: 2 }}
      >
        Delete Field
      </Button>
    </Paper>
  );
};
